import React, { useCallback, useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import {
  DataGrid,
  GridActionsCellItem,
  gridClasses,
  gridDensitySelector,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
  GridToolbarQuickFilter,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import singhCoinLogo from "assets/images/favicon.png";
import { format, isEqual } from "date-fns";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import reportsLineChartData from "layouts/dashboard/data/reportsLineChartData";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { getMyEvents } from "services/contract";
import TablePaginationCustom from "components/Pagination";
import MDButton from "components/MDButton";
import { useNavigate } from "react-router-dom";
import { getUserAnalytics } from "services/contract";
import { Edit, Visibility } from "@mui/icons-material";
import { updateEvent } from "services/contract";
import { getMyTodayEvents } from "services/contract";
import { getMyUpcomingEvents } from "services/contract";
import { getMyPastEvents } from "services/contract";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDDatePicker from "components/MDDatePicker";
import MDTimePicker from "components/MDTimePicker";
import { toast } from "react-toastify";

const HIDE_COLUMNS = { category: false };

export function formatTimeTo12Hour(currentHours, currentMinutes) {
  // Determine AM or PM suffix
  const suffix = currentHours < 12 ? "AM" : "PM";

  // Convert hours from 24-hour format to 12-hour format
  const formattedHours = currentHours % 12 || 12; // Convert 0 to 12 for 12 AM

  // Format minutes to always be two digits
  const formattedMinutes = currentMinutes.toString().padStart(2, "0");

  // Combine into a formatted string
  return `${formattedHours}:${formattedMinutes} ${suffix}`;
}
const formatDateToString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based, so add 1
  const day = String(date.getDate()).padStart(2, "0"); // Pad single digits with leading zero
  return `${year}-${month}-${day}`; // Format as YYYY-MM-DD
};

// const CONFIG = {
//   site: {
//     name: "Minimals",
//     serverUrl: import.meta.env.VITE_SERVER_URL ?? "",
//     assetURL: import.meta.env.VITE_ASSET_URL ?? "",
//     basePath: import.meta.env.VITE_BASE_PATH ?? "",
//     version: packageJson.version,
//   },
// };

const HIDE_COLUMNS_TOGGLABLE = ["category", "actions"];

// ----------------------------------------------------------------------

function useBoolean(defaultValue = false) {
  const [value, setValue] = useState(defaultValue);

  const onTrue = useCallback(() => {
    setValue(true);
  }, []);

  const onFalse = useCallback(() => {
    setValue(false);
  }, []);

  const onToggle = useCallback(() => {
    setValue((prev) => !prev);
  }, []);

  const memoizedValue = useMemo(
    () => ({
      value,
      onTrue,
      onFalse,
      onToggle,
      setValue,
    }),
    [value, onTrue, onFalse, onToggle, setValue]
  );

  return memoizedValue;
}

function useSetState(initialState) {
  const [state, set] = useState(initialState);

  const canReset = !isEqual(state, initialState);

  const setState = useCallback((updateState) => {
    set((prevValue) => ({ ...prevValue, ...updateState }));
  }, []);

  const setField = useCallback(
    (name, updateValue) => {
      setState({ [name]: updateValue });
    },
    [setState]
  );

  const onResetState = useCallback(() => {
    set(initialState);
  }, [initialState]);

  const memoizedValue = useMemo(
    () => ({
      state,
      setState,
      setField,
      onResetState,
      canReset,
    }),
    [canReset, onResetState, setField, setState, state]
  );

  return memoizedValue;
}

function EmptyContent({
  sx,
  imgUrl,
  action,
  filled,
  slotProps,
  description,
  title = "No data",
  ...other
}) {
  return (
    <Stack
      flexGrow={1}
      alignItems="center"
      justifyContent="center"
      sx={{
        px: 3,
        height: 1,
        ...(filled && {
          borderRadius: 2,
          bgcolor: (theme) => varAlpha(theme.vars.palette.grey["500Channel"], 0.04),
          border: (theme) => `dashed 1px ${varAlpha(theme.vars.palette.grey["500Channel"], 0.08)}`,
        }),
        ...sx,
      }}
      {...other}
    >
      <Box
        component="img"
        alt="empty content"
        src={imgUrl ?? `/assets/icons/empty/ic-content.svg`}
        sx={{ width: 1, maxWidth: 160, ...slotProps?.img }}
      />

      {title && (
        <Typography
          variant="h6"
          component="span"
          sx={{ mt: 1, textAlign: "center", ...slotProps?.title, color: "text.disabled" }}
        >
          {title}
        </Typography>
      )}

      {description && (
        <Typography
          variant="caption"
          sx={{ mt: 1, textAlign: "center", color: "text.disabled", ...slotProps?.description }}
        >
          {description}
        </Typography>
      )}

      {action && action}
    </Stack>
  );
}

function Dashboard() {
  const { sales, tasks } = reportsLineChartData;
  const navigate = useNavigate();
  const [analyticsData, setAnalyticsData] = useState();

  function applyFilter({ inputData, filters }) {
    const { stock, publish } = filters;

    if (stock.length) {
      inputData = inputData.filter((product) => stock.includes(product.inventoryType));
    }

    if (publish.length) {
      inputData = inputData.filter((product) => publish.includes(product.publish));
    }

    return inputData;
  }

  const confirmRows = useBoolean();

  // const router = useRouter();
  const [loading, setLoading] = useState(false);
  // const { products, productsLoading } = useGetProducts();

  const filters = useSetState({ publish: [], stock: [] });

  const [tableData, setTableData] = useState([]);

  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const [filterButtonEl, setFilterButtonEl] = useState();
  //  (useState < HTMLButtonElement) | (null > null);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState(HIDE_COLUMNS);

  const canReset = filters.state.publish.length > 0 || filters.state.stock.length > 0;

  const dataFiltered = applyFilter({ inputData: tableData, filters: filters.state });

  function formatDate(date) {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // Months are zero-based
    const day = String(date.getUTCDate()).padStart(2, "0");
    let hours = date.getUTCHours() + 5; // Add 5 hours for GMT+5
    let minutes = date.getUTCMinutes() + 30; // Add 30 minutes for GMT+5:30

    // Handle overflow for minutes
    let overflowHours = 0;
    if (minutes >= 60) {
      overflowHours = Math.floor(minutes / 60); // Calculate overflow hours
      minutes = minutes % 60; // Get remaining minutes
    }

    // Handle overflow for hours
    hours = (hours + overflowHours) % 24; // Wrap around hours
    const formattedHours = String(hours).padStart(2, "0");
    const formattedMinutes = String(minutes).padStart(2, "0");

    // Construct final formatted date string
    return `${year}-${month}-${day} ${formattedHours}:${formattedMinutes} GMT+5:30`;
  }

  const [openDialog, setOpenDialog] = useState(false);
  const [initialEvent, setInitialEvent] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [eventDetails, setEventDetails] = useState(selectedEvent || {});
  const [previewImage, setPreviewImage] = useState(selectedEvent?.photo || "");

  // Initial preview from selected event

  const handleEditClick = (eventData) => {
    setSelectedEvent(eventData);

    setInitialEvent(eventData);

    if (new Date(eventData?.eventDateTime) > new Date()) {
      setOpenDialog(true);
    } else {
      toast.error("Event is expired");
      console.log("event is expired");
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    const res = await updateEvent({
      photo: selectedEvent?.photo,
      id: selectedEvent?._id,
      price: Number(ticketPrice),

      eventDateTime: finalDate,
      venue: venue,
      totalTickets: numberOfTickets,
      description: description,
    });

    fetchEvents(currentPage, rowsPerPage);
    handleCloseDialog();
  };

  const handleCloseDialog = () => {
    setOpenDialog(false); // Close the dialog
    setSelectedEvent(null);
    setPreviewImage("");
    // Clear the selected event data
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setPreviewImage(imageUrl);
      setSelectedEvent((prev) => ({
        ...prev,
        photo: file, // Store the file directly
      }));
      setEventDetails((prev) => ({
        ...prev,
        photo: file,
      }));
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Event Name",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "createdAt",
      headerName: "Created At",
      width: 260,
      renderCell: (params) => {
        const timestamp = new Date(params.row?.eventDateTime);

        if (isNaN(timestamp)) {
          return "Invalid date"; // Handle invalid timestamp
        }

        return formatDate(new Date(timestamp)); // Pass valid timestamp to formatDate
      },
      // params.value,
      // (new Date(params.value), "yyyy-MM-dd HH:mm"),
    },
    // {
    //   field: "description",
    //   headerName: "Description",
    //   width: 180,
    // },
    {
      field: "price",
      headerName: "Price",
      width: 200,
      renderCell: (params) => {
        // Assuming price is a number
        return `$${Number(params.value).toFixed(2)}`; // Format price with $ sign and 2 decimal places
      },
    },
    {
      field: "ticketsAvailable",
      headerName: "Tickets Available",
      width: 200,
    },
    {
      field: "ticketsSold",
      headerName: "Tickets Sold",
      width: 200,
      renderCell: (params) => {
        // console.log("@@", params?.row?.totalTickets, params?.row?.ticketsAvailable);
        // Assuming price is a number
        return `${params?.row?.ticketsAvailable - params?.row?.totalTickets}`; // Format price with $ sign and 2 decimal places
      },
    },
    {
      // field: "action",
      // headerName: "Actions",
      // width: 150,
      type: "actions",
      field: "actions",
      headerName: " ",
      align: "right",
      headerAlign: "right",
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,

      getActions: (params) => [
        <GridActionsCellItem
          showInMenu
          icon={
            <Visibility
              sx={{
                color: "white",
                "& .MuiMenuItem-root": { backgroundColor: "black !important" },
              }}
            />
          }
          label="View Event"
          color="white"
          sx={{ "& .MuiMenuItem-root": { backgroundColor: "black !important" } }}
          onClick={() => navigate(`/dashboard/event-details/${params?.id}`)}
          // onClick={() => handleViewRow(params.row.id)}
        />,
        <GridActionsCellItem
          showInMenu
          icon={<Edit />}
          label="Edit Event"
          color="white"
          sx={{ "& .MuiMenuItem-root": { backgroundColor: "black !important" } }}
          // onClick={() => handleViewRow(params.row.id)}
          onClick={() => handleEditClick(params?.row)}
        />,
      ],
    },
  ];

  const getTogglableColumns = () =>
    columns
      .filter((column) => !HIDE_COLUMNS_TOGGLABLE.includes(column.field))
      .map((column) => column.field);

  function FiltersResult({ totalResults, onReset, sx, children }) {
    return (
      <Box sx={sx}>
        <Box sx={{ mb: 1.5, typography: "body2" }}>
          <strong>{totalResults}</strong>
          <Box component="span" sx={{ color: "text.secondary", ml: 0.25 }}>
            results found
          </Box>
        </Box>

        <Box flexGrow={1} gap={1} display="flex" flexWrap="wrap" alignItems="center">
          {children}

          <Button
            color="error"
            onClick={onReset}
            // startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
          >
            Clear
          </Button>
        </Box>
      </Box>
    );
  }

  function FiltersBlock({ label, children, isShow, sx }) {
    if (!isShow) {
      return null;
    }

    return (
      <Box
        gap={1}
        display="flex"
        sx={{
          p: 1,
          borderRadius: 1,
          overflow: "hidden",
          // border: (theme) => `dashed 1px ${theme.vars.palette.divider}`,
          ...sx,
        }}
      >
        <Box
          component="span"
          sx={{
            height: 24,
            lineHeight: "24px",
            // fontSize: (theme) => theme.typography.subtitle2.fontSize,
            // fontWeight: (theme) => theme.typography.subtitle2.fontWeight,
          }}
        >
          {label}
        </Box>
        <Box gap={1} display="flex" flexWrap="wrap">
          {children}
        </Box>
      </Box>
    );
  }

  function ProductTableFiltersResult({ filters, totalResults, sx }) {
    const handleRemoveStock = useCallback(
      (inputValue) => {
        const newValue = filters.state.stock.filter((item) => item !== inputValue);

        filters.setState({ stock: newValue });
      },
      [filters]
    );

    const handleRemovePublish = useCallback(
      (inputValue) => {
        const newValue = filters.state.publish.filter((item) => item !== inputValue);

        filters.setState({ publish: newValue });
      },
      [filters]
    );

    return (
      <FiltersResult totalResults={totalResults} onReset={filters.onResetState} sx={sx}>
        <FiltersBlock label="Stock:" isShow={!!filters.state.stock.length}>
          {filters.state.stock.map((item) => (
            <Chip
              {...chipProps}
              key={item}
              label={sentenceCase(item)}
              onDelete={() => handleRemoveStock(item)}
            />
          ))}
        </FiltersBlock>

        <FiltersBlock label="Publish:" isShow={!!filters.state.publish.length}>
          {filters.state.publish.map((item) => (
            <Chip
              {...chipProps}
              key={item}
              label={sentenceCase(item)}
              onDelete={() => handleRemovePublish(item)}
            />
          ))}
        </FiltersBlock>
      </FiltersResult>
    );
  }
  function CustomSelector() {
    const apiRef = useGridApiContext();
    const densityValue = useGridSelector(apiRef, gridDensitySelector);
    const [anchorEl, setAnchorEl] = React.useState(null);

    const handleOpen = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleChange = async (eventType) => {
      handleClose();

      // apiRef.current.setDensity(newDensity);
      if (eventType === "ongoing") {
        const data = await getMyTodayEvents(currentPage, rowsPerPage);
        setTableData(data?.data);
      } else if (eventType === "upcoming") {
        const data = await getMyUpcomingEvents(currentPage, rowsPerPage);
        setTableData(data?.data);
      } else if (eventType === "past") {
        const data = await getMyPastEvents(currentPage, rowsPerPage);

        setTableData(data?.data);
      } else {
        fetchEvents(currentPage, rowsPerPage);
      }
    };

    return (
      <>
        <Button onClick={handleOpen}>Filters</Button>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
          <MenuItem
            onClick={() => handleChange("ongoing")}
            // selected={densityValue === ""}
          >
            Ongoing events
          </MenuItem>
          <MenuItem
            onClick={() => handleChange("upcoming")}
            // selected={densityValue === "standard"}
          >
            Upcoming events
          </MenuItem>
          <MenuItem
            onClick={() => handleChange("past")}
            // selected={densityValue === "comfortable"}
          >
            Past events
          </MenuItem>
          <MenuItem
            onClick={() => handleChange("all")}
            // selected={densityValue === "comfortable"}
          >
            All events
          </MenuItem>
        </Menu>
      </>
    );
  }

  function CustomToolbar({
    filters,
    canReset,
    selectedRowIds,
    filteredResults,
    setFilterButtonEl,
    onOpenConfirmDeleteRows,
  }) {
    return (
      <>
        <GridToolbarContainer>
          {/* <ProductTableToolbar
            filters={filters}
            options={{ stocks: PRODUCT_STOCK_OPTIONS, publishs: PUBLISH_OPTIONS }}
          /> */}

          <Stack
            spacing={1}
            flexGrow={1}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            width={"100%"}
          >
            {/* {!!selectedRowIds?.length && (
              <MDButton
                size="small"
                color="error"
                // startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                onClick={onOpenConfirmDeleteRows}
              >
                Delete ({selectedRowIds?.length})
              </MDButton>
            )} */}
            <GridToolbarQuickFilter
              sx={{
                width: "100%",
                "& .MuiInputBase-root": {
                  border: "none", // Remove any border
                  backgroundColor: "transparent", // Ensure background is transparent
                  "&:before": {
                    border: "none", // Remove the default underline
                    content: "none", // Ensure content is removed
                  },
                  "&:after": {
                    border: "none", // Remove the default underline when focused
                    content: "none", // Ensure content is removed
                  },
                  "&:hover": {
                    "&:before": {
                      border: "none", // No underline on hover
                    },
                  },
                  "& .MuiInputBase-input": {
                    border: "none", // Remove any underline from the input itself
                    "&:focus": {
                      outline: "none", // Remove outline on focus
                    },
                  },
                },
                "& .MuiInput-underline:before": {
                  display: "none", // Completely remove the before element (underline)
                },
                "& .MuiInput-underline:after": {
                  display: "none", // Completely remove the after element (underline when focused)
                },
                "& .MuiInput-underline:hover:before": {
                  display: "none", // Ensure no underline appears on hover
                },
              }}
            />
            <GridToolbarColumnsButton ref={setFilterButtonEl} />
            {/* <Box>
              <GridToolbarColumnsButton /> */}
            {/* <GridToolbarFilterButton ref={setFilterButtonEl} /> */}
            {/* </Box> */}
            <CustomSelector />
            <GridToolbarExport
              sx={{
                color: "white !important", // Set text color of export button to white
                "& .MuiButton-root": {
                  color: "white !important", // Ensure the button text is white
                },
                "& .MuiSvgIcon-root": {
                  color: "white !important", // Set export icon color to white
                },
              }}
            />
          </Stack>
        </GridToolbarContainer>

        {canReset && (
          <ProductTableFiltersResult
            filters={filters}
            totalResults={filteredResults}
            sx={{ p: 2.5, pt: 0 }}
          />
        )}
      </>
    );
  }

  const CustomToolbarCallback = useCallback(
    () => (
      <CustomToolbar
        filters={filters}
        canReset={canReset}
        selectedRowIds={selectedRowIds}
        setFilterButtonEl={setFilterButtonEl}
        filteredResults={dataFiltered?.length}
        onOpenConfirmDeleteRows={confirmRows.onTrue}
      />
    ),

    [filters.state, selectedRowIds]
  );

  const [currentPage, setCurrentPage] = useState(0); // Current page
  const [rowsPerPage, setRowsPerPage] = useState(5); // Rows per page
  const [totalPages, setTotalPages] = useState(0);

  const fetchEvents = async (page, pageSize) => {
    const eventsData = await getMyEvents(page, pageSize);
    setTableData(eventsData.data);

    setTotalPages(eventsData.totalEvents);
  };

  const fetchUserAnalytics = async () => {
    const userAnalytics = await getUserAnalytics();
    setAnalyticsData(userAnalytics?.data);
  };

  useEffect(() => {
    fetchEvents(currentPage, rowsPerPage);
    fetchUserAnalytics();
    // eslint-disable-next-line
  }, [currentPage, rowsPerPage]);

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0); // Reset to first page
  };

  const [date, setDate] = useState(new Date());
  const [time, setTime] = useState(null); // State for time

  const [errorMsg, setErrorMsg] = useState({
    name: { errorMsg: "" },
    venue: { errorMsg: "" },
    description: { errorMsg: "" },
    numberOfTickets: { errorMsg: "" },
    ticketPrice: { errorMsg: "" },
    date: { errorMsg: "" },
    time: { errorMsg: "" },
    category: { errorMsg: "" },
  });
  const [loader, setLoader] = useState(false);
  const [ticketType, setTicketType] = useState("Free");
  const [name, setName] = useState("");

  const [eventType, setEventType] = useState(
    selectedEvent?.venue === "Online" ? "Online" : "Offline"
  );
  const [description, setDescription] = useState(selectedEvent?.description);
  const [numberOfTickets, setNumberOfTickets] = useState(selectedEvent?.totalTickets);

  const [venue, setVenue] = useState(selectedEvent?.venue);
  const [venueDetails, setVenueDetails] = useState("");
  const [ticketPrice, setTicketPrice] = useState(selectedEvent?.price);
  const [finalDate, setFinalDate] = useState(null);

  let currentDate = new Date(selectedEvent?.eventDateTime);
  currentDate = `${currentDate.getFullYear()}-${
    currentDate.getMonth() + 1 <= 9
      ? "0" + (currentDate.getMonth() + 1)
      : currentDate.getMonth() + 1
  }-${currentDate.getDate() <= 9 ? "0" + currentDate.getDate() : currentDate.getDate()}`;

  const currentHours = new Date(selectedEvent?.eventDateTime).getHours();
  const currentMinutes = new Date(selectedEvent?.eventDateTime).getMinutes();

  const formattedTime = formatTimeTo12Hour(currentHours, currentMinutes);

  const capitalizeFirstLetter = (value) => {
    return value && value[0].toUpperCase() + value.slice(1);
  };

  const handleChangeInput = (name, value) => {
    // console.log("namenvalue", name, value);
    if (name === "name") {
      setName(capitalizeFirstLetter(value));
    } else if (name === "description") {
      setDescription(value);
    } else if (name === "numberOfTickets") {
      setNumberOfTickets(value);
    } else if (name === "category") {
      setCategory(value);
    } else if (name === "date") {
      setDate(value);
    } else if (name === "venue") {
      setVenue(value);
    } else if (name === "venueDetails") {
      setVenueDetails(value);
    } else if (name === "ticketPrice") {
      setTicketPrice(value);
    } else {
      console.log("inputError");
    }
  };

  const handleFree = () => {
    setTicketType("Free");
    setTicketPrice(0);
  };

  const handleEventType = (name) => {
    if (name === "onlineEvent") {
      setEventType("Online");
      setVenue({ label: "Online", value: "Online" });
    } else {
      setEventType("Offline");
      setVenue("");
    }
  };

  const handleDateChange = (newDate) => {
    setDate(newDate[0]);
    console.log("@here123", formattedTime, updateDateTimeWithStringTime(newDate[0], formattedTime));
    combineDateTime(
      newDate[0],
      finalDate ? finalDate : updateDateTimeWithStringTime(newDate[0], formattedTime)
    );
  };

  const handleTimeChange = (newTime) => {
    const currentDate = new Date();

    // current date and time
    console.log("formateed", formattedTime);
    const date1 = updateDateTimeWithStringTime(date, formattedTime);
    console.log("@1", date1);

    const selectedDateTime = new Date(
      date1.getFullYear(),
      date1.getMonth(),
      date1.getDate(),
      newTime.getHours(),
      newTime.getMinutes()
    );

    // Check if the selected time is in the past
    if (selectedDateTime < currentDate) {
      setErrorMsg((prevErrorMsg) => ({
        ...prevErrorMsg,
        time: { errorMsg: "You cannot select a time in the past" },
      }));
      return;
    }

    // Reset the error message if time is valid
    setErrorMsg((prevErrorMsg) => ({
      ...prevErrorMsg,
      time: { errorMsg: "" },
    }));

    setTime(newTime);

    // setDateString(date1);
    // setTimeString(newTime);
    // setA(date1)
    combineDateTime(date1, newTime);
  };

  const combineDateTime = (date, time) => {
    if (date && time) {
      const combinedDateTime = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        time.getHours(),
        time.getMinutes()
      );

      handleChangeInput("date", combinedDateTime);

      setFinalDate(combinedDateTime);
      console.log("@here", combinedDateTime);
    }
  };

  useEffect(() => {
    setEventType(selectedEvent?.venue === "Online" ? "Online" : "Offline");
    setDescription(selectedEvent?.description);
    setNumberOfTickets(selectedEvent?.totalTickets);
    setTicketPrice(selectedEvent?.price);

    setDate(currentDate);

    setTicketType(selectedEvent?.price === 0 ? "Free" : "Paid");
    setVenue(selectedEvent?.venue);
  }, [selectedEvent]);

  const updateDateTimeWithStringTime = (inputDate, timeString) => {
    let updatedDate;

    // Parse the time string (e.g., '11:20 PM')
    const [time, modifier] = timeString.split(" ");
    let [hours, minutes] = time.split(":").map(Number);

    // Convert 12-hour format to 24-hour format
    if (modifier === "PM" && hours < 12) {
      hours += 12; // Convert PM to 24-hour format
    }
    if (modifier === "AM" && hours === 12) {
      hours = 0; // Midnight case
    }

    // Check if inputDate is a Date object
    if (inputDate instanceof Date) {
      // If it's a Date object, create a new Date instance with the same date and updated time
      updatedDate = new Date(
        inputDate.getFullYear(),
        inputDate.getMonth(),
        inputDate.getDate(),
        hours,
        minutes
      );
    } else if (typeof inputDate === "string") {
      // If it's a string, assume it is in the format 'YYYY-MM-DD'
      // const dateParts = inputDate.split('-');
      // const year = parseInt(dateParts[0], 10);
      // const month = parseInt(dateParts[1], 10) - 1; // Months are 0-indexed in JavaScript
      // const day = parseInt(dateParts[2], 10);
      // updatedDate = new Date(year, month, day, hours, minutes);
      updatedDate = new Date(`${inputDate} ${timeString}`);
    } else {
      throw new Error("Invalid date format"); // Handle unexpected formats
    }

    console.log("@value", updatedDate);
    return updatedDate;
  };

  console.log(
    "@herefinal",
    // updateDateTimeWithStringTime(date, formattedTime),
    // finalDate

    // formattedTime,
    // "#ctest",

    // initialEvent?.photo === selectedEvent?.photo,
    // ticketPrice === selectedEvent?.price,
    // description === selectedEvent?.description,
    // venue === selectedEvent?.venue,

    // finalDate === null,

    // numberOfTickets === selectedEvent?.totalTickets
    finalDate,
    new Date(finalDate),
    new Date(selectedEvent?.eventDateTime),
    finalDate === null
      ? finalDate === null
      : new Date(finalDate).getTime() == new Date(selectedEvent?.eventDateTime).getTime()
    // finalDate,
    // new Date(selectedEvent?.eventDateTime)
  );
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                // color="dark"
                icon="weekend"
                title="Total Events"
                count={analyticsData?.totalEvents}

                // percentage={{ color: "success", amount: "+55%", label: "than last week" }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                icon="local_activity"
                title="Total Ticket Sold"
                count={analyticsData?.totalTicketSold}
                // percentage={{ color: "success", amount: "+3%", label: "than last month" }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                // color="dark"
                icon="store"
                title="Revenue"
                count={analyticsData?.totalRevenue}
                // percentage={{ color: "success", amount: "+1%", label: "than yesterday" }}
              />
            </MDBox>
          </Grid>
        </Grid>

        <MDBox>
          {/* <Grid container spacing={3}> */}
          <Grid item xs={12} md={6} lg={8}>
            <DataGrid
              checkboxSelection
              disableRowSelectionOnClick
              rows={tableData}
              columns={columns}
              pageSizeOptions={[5, 10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              getRowId={(row) => row._id}
              totalPages={totalPages}
              paginationMode="server"
              disableColumnMenu
              onRowSelectionModelChange={(newSelectionModel) =>
                setSelectedRowIds(newSelectionModel)
              }
              // onPaginationModelChange={handlePaginationModelChange}
              slots={{
                toolbar: CustomToolbarCallback,

                noRowsOverlay: () => <EmptyContent />,
                noResultsOverlay: () => <EmptyContent title="No results found" />,
                pagination: () => (
                  <TablePaginationCustom
                    count={totalPages}
                    page={currentPage}
                    rowsPerPage={rowsPerPage}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    dense
                  />
                ),
              }}
              slotProps={{
                panel: {
                  anchorEl: filterButtonEl,
                  sx: {
                    "& .MuiPaper-root": {
                      backgroundColor: "#00303a !important", // Set the dialog's background color to red
                      // border: "1px solid white",
                    },
                  },
                },
                toolbar: {
                  setFilterButtonEl,
                  columnsButton: {
                    sx: {
                      "& .MuiPaper-root-MuiDataGrid-paper": {
                        backgroundColor: "#00303a !important", // Set background color to red for the Paper component
                      },
                    },
                  },
                },
                columnsManagement: { getTogglableColumns },
              }}
              // slots={{
              //   toolbar: CustomToolbar,
              // }}
              sx={{
                height: 412, // Fixed height for the DataGrid
                overflowY: "auto", // Enable vertical scrolling
                backgroundColor: "black",
                borderRadius: "8px",
                border: "1px solid white",

                [`& .${gridClasses.cell}`]: {
                  alignItems: "center",
                  display: "inline-flex",
                  outline: "none",
                  border: "none",
                },

                [`& .MuiDataGrid-columnHeaders`]: {
                  color: "#fff",
                  borderBottom: "none",
                },
                "& .MuiDataGrid-columnHeader": {
                  borderBottom: "none", // Ensure no border for individual column headers
                  color: "white", // Ensure text color of column headers is white
                },

                "& .MuiDataGrid-container--top [role=row]": {
                  backgroundColor: "#F57C00 !important",
                },

                "& .MuiDataGrid-cell:focus-within": {
                  outline: "none",
                },

                "& .MuiDataGrid-row": {
                  border: "none", // Remove border from rows
                  outline: "none",
                },
                "& .MuiDataGrid-cell": {
                  border: "none",
                  color: "#fff",
                  // Remove border from data cells
                },

                [`& .MuiTablePagination-toolbar`]: {
                  color: "#fff", // Change text color of "Rows per page" and pagination buttons
                },

                "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
                  outline: "none",

                  // Removes the default red focus outline or border
                },
              }}
            />
          </Grid>
          <Dialog
            open={openDialog}
            onClose={handleCloseDialog}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              style: {
                backgroundColor: "#1a1a1a", // Set dialog background color to black
                color: "white",
                // border: "1px solid white",
                // Set text color to white for contrast
              },
            }}
          >
            <DialogTitle>Edit Event</DialogTitle>
            <DialogContent>
              <form onSubmit={(e) => handleEdit(e)}>
                {/* Show initial event image if no new image is uploaded */}
                {!previewImage && selectedEvent?.photo && (
                  <div style={{ position: "relative", marginBottom: "16px" }}>
                    <img
                      style={{
                        width: "200px",
                        height: "200px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        display: "block",
                        margin: "0 auto",
                      }}
                      src={selectedEvent.photo}
                      alt="Event Logo"
                    />
                    {/* Circular "Edit Image" overlay button */}
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "white",
                        backgroundColor: "rgba(0, 0, 0, 0.6)",
                        padding: "10px",
                        borderRadius: "50%", // Make the button circular
                        cursor: "pointer",
                        textAlign: "center",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                      onClick={() => document.querySelector('input[type="file"]').click()} // Trigger file input click
                    >
                      <Edit style={{ fontSize: "24px" }} /> {/* Make the icon bigger */}
                    </div>
                  </div>
                )}

                {/* Preview the uploaded image */}
                {previewImage && (
                  <div style={{ position: "relative", marginBottom: "16px" }}>
                    <img
                      src={previewImage}
                      alt="Event Preview"
                      style={{
                        width: "200px",
                        height: "200px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        display: "block",
                        margin: "0 auto",
                      }}
                    />
                    {/* Circular "Change Image" overlay button */}
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        color: "white",
                        backgroundColor: "rgba(0, 0, 0, 0.6)",
                        padding: "10px",
                        borderRadius: "50%", // Circular button
                        cursor: "pointer",
                        textAlign: "center",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                      onClick={() => document.querySelector('input[type="file"]').click()} // Trigger file input click
                    >
                      <Edit style={{ fontSize: "24px" }} />
                    </div>
                  </div>
                )}

                {/* <InputLabel sx={{ marginBottom: 2, color: "white" }}>Event Image</InputLabel> */}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: "none" }}
                />

                {/* <TextField
                  label="Price"
                  type="number"
                  fullWidth
                  margin="normal"
                  value={selectedEvent?.price}
                  onChange={(e) => setSelectedEvent({ ...selectedEvent, price: e.target.value })}
                  InputLabelProps={{ style: { color: "white" } }} // White label text for the input
                  InputProps={{
                    style: { color: "white" }, // White text for input
                  }}
                /> */}

                <MDBox sx={{ marginBottom: "0.5rem" }}>
                  <MDBox sx={{ display: "flex", gap: "10px" }}>
                    <MDButton
                      name="offlineEvent"
                      variant={eventType === "Offline" ? "contained" : "outlined"}
                      size="small"
                      onClick={(e) => handleEventType(e.target.name)}
                    >
                      Offline
                    </MDButton>
                    <MDButton
                      name="onlineEvent"
                      variant={eventType === "Online" ? "contained" : "outlined"}
                      size="small"
                      onClick={(e) => handleEventType(e.target.name)}
                    >
                      Online
                    </MDButton>
                  </MDBox>
                </MDBox>
                {eventType === "Offline" ? (
                  <>
                    <MDBox sx={{ marginBottom: "0.5rem" }}>
                      <GooglePlacesAutocomplete
                        error={errorMsg.venue.errorMsg.length > 0 || null}
                        apiKey="AIzaSyDnRwrG8QHtzehsIYx-D3GuqjYRCT-qH7U"
                        selectProps={{
                          placeholder: "Select venue",
                          styles: {
                            placeholder: (provided) => ({
                              ...provided,
                              fontSize: "medium",
                            }),
                            control: (provided, state) => ({
                              ...provided,
                              backgroundColor: "transparent",
                              borderWidth: "0.1px",
                              borderStyle: "ridge",
                              // borderTopStyle: "groove",
                            }),
                            menu: (provided) => ({
                              ...provided,
                              backgroundColor: "#121212",
                            }),
                            input: (provided, state) => ({
                              ...provided,
                              color: "white",
                            }),
                            option: (provided) => ({
                              ...provided,
                              color: "white",
                              cursor: "pointer",
                              "&:hover, &:focus, &:target, &:focus-within": {
                                backgroundColor: "#FF9607",
                              },
                            }),
                            singleValue: (provided) => ({
                              ...provided,
                              color: "white",
                            }),
                          },
                          // isClearable: true,
                          value: venue,
                          // onChange: setVenue,
                          onChange: (e) => {
                            handleChangeInput("venue", e);
                          },
                        }}
                      ></GooglePlacesAutocomplete>
                      <MDTypography fontWeight="light" fontSize="0.7rem" sx={{ color: "red" }}>
                        {errorMsg.venue.errorMsg}
                      </MDTypography>
                    </MDBox>
                    <MDBox sx={{ marginBottom: "0.5rem" }}>
                      <MDInput
                        fullWidth
                        placeholder="Venue details, Landmark, etc (optional)"
                        name="venueDetails"
                        value={venueDetails}
                        // error={errorMsg.numberOfTickets.errorMsg.length > 0}
                        onChange={(e) => handleChangeInput(e.target.name, e.target.value)}
                      />
                    </MDBox>
                  </>
                ) : null}

                <MDBox sx={{ marginBottom: "0.5rem" }}>
                  <MDInput
                    fullWidth
                    placeholder="Enter number of tickets"
                    type="number"
                    inputProps={{ min: "1" }}
                    name="numberOfTickets"
                    value={numberOfTickets}
                    error={errorMsg.numberOfTickets.errorMsg.length > 0}
                    onChange={(e) => handleChangeInput(e.target.name, e.target.value)}
                  />
                  <MDTypography fontWeight="light" fontSize="0.7rem" sx={{ color: "red" }}>
                    {errorMsg.numberOfTickets.errorMsg}
                  </MDTypography>
                </MDBox>

                <MDBox sx={{ marginBottom: "0.5rem" }}>
                  <MDDatePicker
                    options={{ minDate: currentDate }}
                    value={date}
                    onChange={handleDateChange}
                  />
                </MDBox>

                <MDBox sx={{ marginBottom: "0.5rem" }}>
                  <MDTimePicker
                    input={{ placeholder: "Select time" }}
                    onChange={handleTimeChange}
                    error={errorMsg.category.errorMsg.length > 0}
                    defaultTime={formattedTime}
                    setDefaultTime={setTime}
                  />
                  <MDTypography fontWeight="light" fontSize="0.7rem" sx={{ color: "red" }}>
                    {errorMsg.time.errorMsg}
                  </MDTypography>
                </MDBox>

                <MDBox sx={{ marginBottom: "0.5rem" }}>
                  <MDBox sx={{ display: "flex", gap: "10px" }}>
                    <MDButton
                      variant={ticketType === "Free" ? "contained" : "outlined"}
                      size="small"
                      onClick={() => handleFree()}
                    >
                      Free
                    </MDButton>
                    <MDButton
                      variant={ticketType === "Paid" ? "contained" : "outlined"}
                      size="small"
                      onClick={() => setTicketType("Paid")}
                    >
                      Paid
                    </MDButton>
                  </MDBox>
                  {ticketType === "Paid" && (
                    <>
                      <MDInput
                        sx={{ marginTop: "10px" }}
                        fullWidth
                        type="number"
                        inputProps={{ min: "0", step: "any" }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <img src={singhCoinLogo} width="18px" style={{}} />
                            </InputAdornment>
                          ),
                        }}
                        name="ticketPrice"
                        placeholder="Enter ticket price"
                        value={ticketPrice}
                        error={errorMsg.ticketPrice.errorMsg.length > 0}
                        onChange={(e) => handleChangeInput(e.target.name, e.target.value)}
                      />
                      <MDTypography fontWeight="light" fontSize="0.7rem" sx={{ color: "red" }}>
                        {errorMsg.ticketPrice.errorMsg}
                      </MDTypography>
                    </>
                  )}
                </MDBox>

                <MDBox sx={{ width: "100%", marginBottom: "3rem" }}>
                  <MDInput
                    fullWidth
                    multiline={true}
                    rows={6}
                    name="description"
                    placeholder="Enter description..."
                    value={description}
                    error={errorMsg.description.errorMsg.length > 0}
                    onChange={(e) => handleChangeInput(e.target.name, e.target.value)}
                  />
                  <MDTypography fontWeight="light" fontSize="0.7rem" sx={{ color: "red" }}>
                    {errorMsg.description.errorMsg}
                  </MDTypography>
                </MDBox>

                <MDBox sx={{ width: "50%" }}>
                  {/* disable if all the form data not filled */}
                  <MDButton
                    variant="contained"
                    disabled={
                      initialEvent?.photo === selectedEvent?.photo &&
                      ticketPrice === selectedEvent?.price &&
                      description === selectedEvent?.description &&
                      venue === selectedEvent?.venue &&
                      (finalDate === null
                        ? finalDate === null
                        : new Date(finalDate).getTime() ==
                          new Date(selectedEvent?.eventDateTime).getTime()) &&
                      numberOfTickets === selectedEvent?.totalTickets
                    }
                    fullWidth
                    type="submit"
                    // onClick={() => handleEdit(e)}
                  >
                    <MDTypography>
                      {loader ? <CircularProgress color="inherit" size={25} /> : "Edit"}
                    </MDTypography>
                  </MDButton>
                </MDBox>
              </form>
            </DialogContent>
          </Dialog>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Dashboard;
